import React from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ServicesProvider } from './contexts/services';
import type { KeycloakInitOptions } from 'keycloak-js';
import keycloak from './keycloak';
import { KeycloakProvider } from './State/Keycloak/KeycloakProvider';
import AuthProvider from './State/Auth/AuthProvider';
import { readStoredSession } from './State/Auth/session';
import GraphProvider from './State/Graph/GraphProvider';
import ImpersonateProvider from './State/Impersonate/ImpersonateProvider';
import PermissionsProvider from './State/Permissions/PermissionsProvider';
import ProfileProvider from './State/Profile/ProfileProvider';
import ReportsProvider from './State/Reports/ReportsProvider';
import SnackbarProvider from './State/Snackbar/SnackbarProvider';
import UsersProvider from './State/Users/UsersProvider';

// Utgångna tokens skickas inte till init; då går uppstarten via check-sso
const storedSession = readStoredSession();

interface ProviderProps {
  children?: React.ReactNode;
}

const initOptions: KeycloakInitOptions = {
  onLoad: 'check-sso',
  token: storedSession?.token,
  refreshToken: storedSession?.refreshToken,
  pkceMethod: 'S256',
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const Providers = ({ children }: ProviderProps) => (
  <>
    <QueryClientProvider client={queryClient}>
      <KeycloakProvider keycloak={keycloak} initOptions={initOptions}>
        <SnackbarProvider>
          <AuthProvider>
            <PermissionsProvider>
              <ImpersonateProvider>
                <GraphProvider>
                  <ProfileProvider>
                    <ReportsProvider>
                      <UsersProvider>
                        <ServicesProvider>{children}</ServicesProvider>
                      </UsersProvider>
                    </ReportsProvider>
                  </ProfileProvider>
                </GraphProvider>
              </ImpersonateProvider>
            </PermissionsProvider>
          </AuthProvider>
        </SnackbarProvider>
      </KeycloakProvider>
    </QueryClientProvider>
  </>
);

export default Providers;
